import React from "react"
import {
	BrowserRouter as Router,
	Redirect,
	Route,
	Switch,
} from "react-router-dom"
import { FormattedMessage, IntlProvider } from "react-intl"
import { ReactQueryDevtools } from "react-query/devtools"
import { ErrorBoundary } from "react-error-boundary"
import { isFeatureEnabled } from "config/feature-flags"

import Home from "components/Home/Home"
import LoginLanding from "components/LoginLanding/LoginLanding"
import LoginValidation from "components/LoginValidation"
import PrivateRoute from "./PrivateRoute"
import NotFound from "components/NotFound/NotFound"
import Header from "components/Header/Header"
import Sidebar from "components/Sidebar/Sidebar"
import ExpirationMessageModal from "components/Shared/Modals/ExpirationMessage"
import UnexpectedError from "components/UnexpectedError"
import {
	ROOT_CONTEXT,
	HOME,
	LOGIN_LANDING,
	NOT_FOUND,
	PINGFED_CALLBACK,
	PHRONESIS_LOGIN,
	RESET_PW,
} from "constants/paths"
import {
	ApplicationContext,
	PrepareContext,
	useApplicationContext,
} from "context/applicationContext"
import {
	FiltersContext,
	PrepareFiltersContext,
} from "components/Shared/GlobalFilter/context/filtersContext"
import getModules from "./config/index"
import { isAccessPrivileges } from "helpers/accessPrivilegesUtils"
import FeedbackFooter from "components/FeedbackFooter"
import Login from "components/Login/Login"
import ResetPassword from "components/ResetPassword/ResetPassword"
import useExternalScripts from "./useExternalScripts"

import "./RootRouter.css"
import { Banner } from "@walmart-web/livingdesign-components"

export default function RootRouter() {
	const navigationContext = PrepareContext()
	const context = PrepareFiltersContext()
	useExternalScripts()

	return (
		<Router basename={ROOT_CONTEXT}>
			<ApplicationContext.Provider value={navigationContext}>
				<FiltersContext.Provider value={context}>
					<IntlProvider
						messages={navigationContext.messages}
						locale={navigationContext.locale}
					>
						<ErrorBoundary FallbackComponent={UnexpectedError}>
							<Switch>
								<Route exact path={LOGIN_LANDING}>
									<ResponsiveComponents className="login-landing">
										<LoginLanding />
									</ResponsiveComponents>
								</Route>
								<Route
									exact
									path={PINGFED_CALLBACK}
									component={LoginValidation}
								/>
								<Route exact path={PHRONESIS_LOGIN} component={Login} />
								<Route exact path={RESET_PW}>
									<ResetPassword />
								</Route>
								<Route path="*">
									<AuthenticatedContent />
								</Route>
							</Switch>
						</ErrorBoundary>
					</IntlProvider>
					<ReactQueryDevtools initialIsOpen={false} />
				</FiltersContext.Provider>
			</ApplicationContext.Provider>
		</Router>
	)
}

interface ResponsiveComponentsProps {
	children?: React.ReactNode
	bannerContent?: React.ReactNode
	isHeaderAuthenticated?: boolean
	className?: string
}

// eslint-disable-next-line complexity
const ResponsiveComponents: React.FC<ResponsiveComponentsProps> = (
	props
): JSX.Element => {
	const { children, bannerContent, isHeaderAuthenticated } = props
	const { expirationMessage, isAuthenticated } = useApplicationContext()
	const variant = isHeaderAuthenticated ? "primary" : "secondary"

	return (
		<div className={props.className}>
			{bannerContent}
			<Header variant={variant} />
			{isAuthenticated && isFeatureEnabled("sessionExpirationMessage") && (
				<ExpirationMessageModal isOpen={expirationMessage} />
			)}
			{children}
		</div>
	)
}

const AuthenticatedContent: React.FC = (): JSX.Element => {
	const navigationContext = PrepareContext()

	if (!navigationContext.isAuthenticated) {
		return (
			<React.Fragment>
				<ResponsiveComponents>
					<div className="body-container">
						<Redirect
							to={{
								pathname: LOGIN_LANDING,
							}}
						/>
					</div>
				</ResponsiveComponents>
				<FeedbackFooter />
			</React.Fragment>
		)
	}

	return (
		<React.Fragment>
			<ResponsiveComponents
				className="main-content"
				isHeaderAuthenticated
				bannerContent={
					isFeatureEnabled("phronesisGlobalAlertMessage") && (
						<Banner
							onClose={() => {}}
							closeButtonProps={{ hidden: true }}
							variant="warning"
							UNSAFE_className="phronesis-alert-message"
						>
							<FormattedMessage id="phronesis.global.alert.message" />
						</Banner>
					)
				}
			>
				<div className="body-container">
					<>
						<Sidebar />
						<Switch>
							<PrivateRoute exact path={HOME} component={Home} />
							{getModules().map(
								({ module, submodule, policies, component, path, ...rest }) =>
									isAccessPrivileges(module, submodule, policies) && (
										<PrivateRoute
											{...rest}
											exact
											path={path}
											key={path}
											component={component || <React.Fragment />}
										/>
									)
							)}
							<Route path={NOT_FOUND} component={NotFound} />
						</Switch>
					</>
				</div>
			</ResponsiveComponents>
			<FeedbackFooter />
		</React.Fragment>
	)
}
